import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { TextField, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableFooter, Typography, IconButton, Dialog, DialogContent, Box, Fade, useTheme, useMediaQuery, Card, CardContent, Divider, Grid } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ProductMaster from '../../ProductMaster';

const DropdownProductCode = forwardRef(({ id, options, value, onSelect, onNewProductAdded, onProductMasterClose, connectionString, searchBy = 'code', label, autoFocus = false }, ref) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md')); // ✅ Detect md and above
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [inputValue, setInputValue] = useState(value || '');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const requiredAsteriskStyle = {
    '& .MuiFormLabel-asterisk': {
      color: 'red',
    },
  };

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus?.(),
  }), []);

  // Auto-focus on mount if autoFocus prop is true
  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus?.();
    }
  }, [autoFocus]);

  useEffect(() => { setInputValue(value || ''); }, [value]);

  // useEffect(() => {
  //   if (inputValue) {
  //     const filtered = options.filter(opt => opt.itemCode.toLowerCase().startsWith(inputValue.toLowerCase()));
  //     setFilteredOptions(filtered);
  //   } else {
  //     setFilteredOptions([]);
  //   }
  // }, [inputValue, options]);

  useEffect(() => {
    if (inputValue) {
      const search = inputValue.toLowerCase();

      const filtered = options.filter(opt => {
        if (searchBy === 'description') {
          return opt.description?.toLowerCase().includes(search);
        } else {
          return opt.itemCode?.toLowerCase().includes(search);
        }
      });

      setFilteredOptions(filtered);
    } else {
      setFilteredOptions([]);
    }
    setHighlightedIndex(0); // Reset highlight when search results change
  }, [inputValue, options, searchBy]);

  const handleFocus = async () => {
    if (onNewProductAdded) {
      await onNewProductAdded(); // Fetch fresh data
    }
    setFilteredOptions(options); // Show all products right after fetch
    setDropdownOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setDropdownOpen(true);
  };

  const handleSelect = (option) => {
    setInputValue(option.itemCode);
    onSelect(option);
    setDropdownOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!dropdownOpen || filteredOptions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = filteredOptions[highlightedIndex];
      if (selected) {
        handleSelect(selected);
      }
    } else if (e.key === 'Escape') {
      setDropdownOpen(false);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    onNewProductAdded?.();
    if (onProductMasterClose) {
      onProductMasterClose();  // ✅ CALL THIS
    }
    if (inputValue) setDropdownOpen(true);
  };

  return (
    <Box sx={{ position: 'relative' }} ref={dropdownRef}>
      <TextField inputRef={inputRef} fullWidth label={label || "Product Code"} id={id} required variant="outlined" value={inputValue} size="small" onChange={handleInputChange} onFocus={handleFocus} InputLabelProps={{ sx: requiredAsteriskStyle }} onKeyDown={handleKeyDown} />

      {/* <Fade in={dropdownOpen && Boolean(inputValue) && !showPopup}>
                <Paper elevation={3} sx={{ position: 'absolute', top: `calc(100% + ${theme.spacing(1)})`, left: 0, width: '100%', minWidth: isMdUp ? 800 : 'auto', maxWidth: '100vw', maxHeight: 400, overflowY: 'auto', borderRadius: 2, boxShadow: theme.shadows[4], zIndex: theme.zIndex.tooltip, mt: 1, px: 1, boxSizing: 'border-box' }} >

                    <Fade in={dropdownOpen && !showPopup}>
                        {isMdUp ? (
                            // ✅ TABLE VIEW FOR MEDIUM & UP
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ borderCollapse: 'separate', borderSpacing: 0, '& thead th': { backgroundColor: '#165bb5', color: '#fff', fontWeight: 600, border: 'none', } }}>
                                        {['Item Code', 'Description', 'Unit', 'GST%', 'Rate', 'Discount%'].map((h, i) => (
                                            <TableCell key={i}><Typography fontWeight="bold">{h}</Typography></TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredOptions.length > 0 ? (
                                        filteredOptions.map((opt, idx) => (
                                            <TableRow hover key={idx} selected={idx === highlightedIndex} onClick={() => handleSelect(opt)} onMouseEnter={() => setHighlightedIndex(idx)} sx={{ cursor: 'pointer' }} >
                                                <TableCell>{opt.itemCode}</TableCell>
                                                <TableCell>{opt.description}</TableCell>
                                                <TableCell>{opt.Unit}</TableCell>
                                                <TableCell>{opt.GST}</TableCell>
                                                <TableCell>{opt.saleRate}</TableCell>
                                                <TableCell>{opt.saleDiscount}</TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                                                <Typography color="text.secondary">No results found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 1 }}>
                                            <IconButton color="primary" onClick={() => setShowPopup(true)}>
                                                <AddCircleOutlineIcon /> Add New Product
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        ) : (
                            // ✅ CARD VIEW FOR SMALL SCREENS
                            filteredOptions.length > 0 ? (
                                filteredOptions.map((opt, idx) => (
                                    <Card key={idx} variant="outlined"
                                        sx={{ mb: 1, cursor: 'pointer', '&:hover': { backgroundColor: '#f0f0f0' } }}
                                        onClick={() => handleSelect(opt)}
                                    >
                                        <CardContent sx={{ py: 1.5 }}>
                                            <Typography variant="subtitle1" fontWeight="bold">{opt.itemCode}</Typography>
                                            <Typography variant="body2" color="text.secondary">{opt.description}</Typography>
                                            <Divider sx={{ my: 1 }} />
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><strong>Unit:</strong> {opt.Unit}</Grid>
                                                <Grid item xs={6}><strong>GST%:</strong> {opt.GST}</Grid>
                                                <Grid item xs={6}><strong>Rate:</strong> ₹{opt.saleRate}</Grid>
                                                <Grid item xs={6}><strong>Disc%:</strong> {opt.saleDiscount}</Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ))
                            ) : (
                                <Typography textAlign="center" sx={{ py: 2 }} color="text.secondary">No results found</Typography>
                            )
                        )}
                    </Fade>
                </Paper>
            </Fade> */}
      {dropdownOpen && inputValue && !showPopup && (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            position: 'absolute',
            width: { xs: '100%', lg: '800px' },
            zIndex: 10,
            border: '1px solid rgba(0,0,0,0.2)',
            borderRadius: 1,
            maxHeight: 300,
            overflowY: 'auto'
          }}
        >
          {isSmallScreen ? (
            /* ================= CARD VIEW (SM / XS) ================= */
            <>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      p: 1.2,
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      backgroundColor: idx === highlightedIndex ? 'action.selected' : 'transparent'
                    }}
                    onMouseDown={() => handleSelect(opt)}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                  >
                    <Box sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      {opt.itemCode}
                    </Box>

                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {opt.description}
                    </Box>

                    <Box sx={{ fontSize: '0.75rem' }}>
                      Unit: {opt.Unit} • GST: {opt.GST}%
                    </Box>

                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      ₹{opt.Rate} • Disc {opt.Discount}%
                    </Box>
                  </Box>
                ))
              ) : (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                  No results found
                </Box>
              )}

              {/* Sticky Add New Product */}
              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  backgroundColor: '#fff',
                  textAlign: 'center',
                  p: 1,
                  fontWeight: 600,
                  color: 'primary.main',
                  cursor: 'pointer',
                  boxShadow: '0 -2px 6px rgba(0,0,0,0.08)'
                }}
                onMouseDown={() => setShowPopup(true)}
              >
                ➕ Add New Product
              </Box>
            </>
          ) : (
            /* ================= TABLE VIEW (MD / LG) ================= */
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Item Code', 'Description', 'Unit', 'GST%', 'Rate', 'Discount%'].map(h => (
                    <TableCell
                      key={h}
                      sx={{
                        backgroundColor: '#E0E0E0',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        width: h === 'Item Code' ? 100 : undefined,
                        minWidth: h === 'Item Code' ? 100 : undefined
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((opt, idx) => (
                    <TableRow
                      key={idx}
                      hover
                      selected={idx === highlightedIndex}
                      sx={{ cursor: 'pointer' }}
                      onMouseDown={() => handleSelect(opt)}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                    >
                      <TableCell sx={{
                        width: 80
                      }}>{opt.itemCode}</TableCell>
                      <TableCell>{opt.description}</TableCell>
                      <TableCell>{opt.Unit}</TableCell>
                      <TableCell>{opt.GST}</TableCell>
                      <TableCell>{opt.Rate}</TableCell>
                      <TableCell>{opt.Discount}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                      No results found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

              <TableFooter
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  backgroundColor: '#fff'
                }}
              >
                <TableRow hover onMouseDown={() => setShowPopup(true)}>
                  <TableCell colSpan={6} align="center" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    ➕ Add New Product
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          )}
        </Paper>
      )}


      <Dialog fullWidth maxWidth="lg" open={showPopup}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          handleClosePopup();
        }}
        PaperProps={{
          sx: {
            width: { xs: "95%", },
            m: { xs: 0 }
          }
        }}
      >
        <DialogContent sx={{ p: { xs: 0 } }}>
          <ProductMaster connectionString={connectionString}
            mode="add"
            onPopupCancel={handleClosePopup}  // ← same handler when the child taps “Cancel” or “Save”
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
});

DropdownProductCode.displayName = 'DropdownProductCode';
export default DropdownProductCode;